import type { IssueTypes } from '@risksmart-app/trpc/src/services/service.types';
import type { NavigationCounts } from '@risksmart-app/trpc/src/types';
import type {
  GetIssueCountQuery,
  Issue_Bool_Exp,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetIssueCountDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { COUNT_STALE_TIME_MS, createQueryHook } from 'src/utils';

import type { NavigationCountArgs } from '../navigation/navigationCounts';
import { toAggregate } from '../navigation/navigationCounts';

type IssueCountArgs = NavigationCountArgs & {
  // The issue Type to count ('issue' for the main badge, or a subtype).
  issueType: IssueTypes;
  // The equivalent Hasura bool_exp (incl. Type) — drives the GraphQL fallback
  // so the flag-gated twin counts identically.
  where?: Issue_Bool_Exp;
};

/**
 * Which `frontend.navigation.counts` field each issue Type reads.
 *
 * One collapsed procedure now serves all eight issue badges, so the Type no
 * longer selects a procedure — it selects a field. Only `issue` is narrowed by
 * the entity filter; the seven subtypes are counted globally server-side, which
 * is the pre-collapse behaviour (the B5 decision).
 *
 * `as const satisfies` keeps the values as literals — so indexing the payload
 * with one resolves to `number | null` rather than the whole union of field
 * types, and no cast is needed — while still failing the build if a name stops
 * matching `NavigationCounts`.
 */
const ISSUE_COUNT_FIELD = {
  issue: 'issue',
  issue_risk_event: 'issueRiskEvent',
  issue_breach_log: 'issueBreachLog',
  issue_consumer_duty: 'issueConsumerDuty',
  issue_customer_trust: 'issueCustomerTrust',
  issue_gdpr_breach_log: 'issueGDPRBreachLog',
  issue_pci_breach_log: 'issuePCIBreachLog',
  issue_sar_log: 'issueSARLog',
} as const satisfies Record<IssueTypes, keyof NavigationCounts>;

export const useGetIssueCount = createQueryHook<
  IssueCountArgs,
  { count: number | null },
  GetIssueCountQuery
>({
  // All eight issue badges share ONE query key, so the badge cannot be
  // distinguished by the input. tanstack's `select` narrows the shared payload
  // per observer — which is why this hook, unlike its 18 siblings, does not use
  // `navigationCountsQueryOptions`. `select` runs on the observer, not the
  // query, so it neither forks the key nor triggers a second request.
  //
  // `select` returns an OBJECT, not the bare count, and that is load-bearing:
  // `createQueryHook` guards its mapper with `if (!trpcData)`, which is falsy —
  // so a scalar `0` would be read as "no data yet" and the badge would render
  // blank instead of `0`. The 18 sibling hooks are immune because their
  // `TTRPCOutput` is the whole (always-truthy) payload; this hook is the only
  // one that narrows, so it is the only one that has to wrap.
  trpcQueryOptions: (trpc, queryArgs) =>
    trpc.frontend.navigation.counts.queryOptions(
      { entityIds: queryArgs?.entityIds },
      {
        select: (data) => ({
          count: data[ISSUE_COUNT_FIELD[queryArgs?.issueType ?? 'issue']],
        }),
      }
    ),
  mapTrpcDataToGraphQL: (data) => ({
    issue_aggregate: { aggregate: toAggregate(data.count) },
  }),
  // Aggregate over a register; mutations invalidate it. See COI-882.
  trpcStaleTime: COUNT_STALE_TIME_MS,
  graphqlDocument: GetIssueCountDocument,
  graphqlVariables: (queryArgs) => ({ where: queryArgs?.where }),
});
